import React, { useMemo } from "react";
import { FiClock, FiEdit2, FiTrash2, FiUser } from "react-icons/fi";
import { format12h, timeToMin, formatDateDMY } from "./timeUtils";
import { formatBookerLabel } from "./bookingDisplay";

const ScheduleTable = ({
  selectedRoom,
  date,
  myBookings = [],
  onEditBooking,
  onDeleteBooking,
}) => {
  const getMeetingTopic = (booking) =>
    booking?.meetingTopic || booking?.topic || booking?.meetingTitle || booking?.purpose || "-";

  const myBookingsSorted = useMemo(() => {
    return (myBookings || [])
      .slice()
      .sort(
        (a, b) =>
          (a.startMin ?? timeToMin(a.startTime)) -
          (b.startMin ?? timeToMin(b.startTime))
      );
  }, [myBookings]);

  const canEdit = typeof onEditBooking === "function";
  const canDelete = typeof onDeleteBooking === "function";

  return (
    <section className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/20 md:p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            รายการจองของฉัน
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
            {selectedRoom?.name || "-"} · วันที่ {formatDateDMY(date) || "-"}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:bg-slate-800 dark:text-slate-300">
          {myBookingsSorted.length} รายการ
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-100 dark:border-slate-800">
        {myBookingsSorted.length === 0 ? (
          <div className="px-3 py-4 text-sm text-gray-500 dark:text-slate-400">
            วันที่เลือกนี้คุณยังไม่มีรายการจอง
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-slate-800">
            {myBookingsSorted.map((booking, idx) => {
              const label = formatBookerLabel(booking);
              const topic = getMeetingTopic(booking);
              const itemKey =
                booking?._id ||
                booking?.id ||
                [booking?.roomId || selectedRoom?.id || "room", booking?.dateKey || date, idx].join("-");

              return (
                <div
                  key={itemKey}
                  className="flex flex-col gap-3 px-3 py-3 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-slate-100">
                      <FiClock className="h-4 w-4 text-indigo-500 dark:text-indigo-300" />
                      <span>
                        {format12h(booking.startTime)} - {format12h(booking.endTime)}
                      </span>
                    </div>
                    <div className="mt-1 truncate text-sm text-gray-700 dark:text-slate-200">
                      {topic}
                    </div>
                    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <FiUser className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{label}</span>
                      </span>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => onEditBooking(booking)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-sky-200 text-sky-600 transition hover:bg-sky-50 dark:border-sky-500/40 dark:text-sky-300 dark:hover:bg-sky-500/10"
                        title="แก้ไขรายการจอง"
                        aria-label="แก้ไขรายการจอง"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => onDeleteBooking(booking)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 text-rose-600 transition hover:bg-rose-50 dark:border-rose-500/40 dark:text-rose-300 dark:hover:bg-rose-500/10"
                        title="ลบรายการจอง"
                        aria-label="ลบรายการจอง"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default ScheduleTable;
